import { ConfirmModal, Input, notify } from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { Button } from '@affine/component/ui/button';
import { TotpSetupDialog } from '@affine/core/components/affine/auth/totp-setup-dialog';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { GraphQLService } from '@affine/core/modules/cloud';
import {
  disableTotpMutation,
  regenerateBackupCodesMutation,
  revokeAllDeviceTrustsMutation,
  revokeDeviceTrustMutation,
  setPreferredTwoFactorMethodMutation,
  totpStatusQuery,
  trustedDevicesQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import * as styles from './two-factor-style.css';

// ─── Types ───────────────────────────────────────────────────────────────────

interface TotpStatus {
  enabled: boolean;
  preferredMethod: string | null;
  verifiedAt: string | null;
  hasBackupCodes: boolean;
  backupCodesCount: number;
}

interface TrustedDevice {
  id: string;
  label: string | null;
  createdAt: string;
  expiresAt: string;
}

// ─── Disable Dialog ─────────────────────────────────────────────────────────

const TotpDisableDialog = ({
  open,
  onOpenChange,
  onComplete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}) => {
  const t = useI18n();
  const gql = useService(GraphQLService);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = useCallback(() => {
    setPassword('');
    setCode('');
    setError('');
  }, []);

  const handleClose = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) reset();
      onOpenChange(isOpen);
    },
    [onOpenChange, reset]
  );

  const onDisable = useAsyncCallback(async () => {
    if (!password || code.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      await gql.gql({
        query: disableTotpMutation,
        variables: { password, code },
      });
      handleClose(false);
      onComplete();
      notify.success({
        title: t['com.streamlain.settings.account.two-factor.disabled-toast'](),
      });
    } catch {
      setError(
        t[
          'com.streamlain.settings.account.two-factor.invalid-password-or-code'
        ]()
      );
    } finally {
      setIsLoading(false);
    }
  }, [gql, password, code, handleClose, onComplete, t]);

  return (
    <ConfirmModal
      open={open}
      onOpenChange={handleClose}
      title={t[
        'com.streamlain.settings.account.two-factor.disable-dialog.title'
      ]()}
      confirmText={t[
        'com.streamlain.settings.account.two-factor.disable-dialog.confirm'
      ]()}
      confirmButtonOptions={{ variant: 'error', loading: isLoading }}
      onConfirm={onDisable}
    >
      <div className={styles.setupStepContent}>
        <p>
          {t[
            'com.streamlain.settings.account.two-factor.disable-dialog.description'
          ]()}
        </p>
        <Input
          autoFocus
          type="password"
          placeholder={t[
            'com.streamlain.settings.account.two-factor.password'
          ]()}
          value={password}
          onChange={setPassword}
        />
        <Input
          placeholder={t['com.streamlain.settings.account.two-factor.code']()}
          value={code}
          onChange={setCode}
          onEnter={onDisable}
          maxLength={6}
          style={{ marginTop: 8 }}
        />
        {error && <p className={styles.errorText}>{error}</p>}
      </div>
    </ConfirmModal>
  );
};

// ─── Regenerate Backup Codes Dialog ─────────────────────────────────────────

const RegenerateBackupCodesDialog = ({
  open,
  onOpenChange,
  onComplete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}) => {
  const t = useI18n();
  const gql = useService(GraphQLService);
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = useCallback(() => {
    setPassword('');
    setCodes(null);
    setError('');
  }, []);

  const handleClose = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        reset();
        if (codes) onComplete();
      }
      onOpenChange(isOpen);
    },
    [onOpenChange, reset, codes, onComplete]
  );

  const onRegenerate = useAsyncCallback(async () => {
    if (!password) return;
    setIsLoading(true);
    setError('');
    try {
      const res = await gql.gql({
        query: regenerateBackupCodesMutation,
        variables: { password },
      });
      setCodes(res.regenerateBackupCodes as string[]);
    } catch {
      setError(
        t['com.streamlain.settings.account.two-factor.incorrect-password']()
      );
    } finally {
      setIsLoading(false);
    }
  }, [gql, password, t]);

  const copyBackupCodes = useCallback(() => {
    if (!codes) return;
    navigator.clipboard
      .writeText(codes.join('\n'))
      .then(() =>
        notify.success({
          title:
            t[
              'com.streamlain.settings.account.two-factor.backup-codes.copied'
            ](),
        })
      )
      .catch(() =>
        notify.error({
          title:
            t[
              'com.streamlain.settings.account.two-factor.backup-codes.copy-failed'
            ](),
        })
      );
  }, [codes, t]);

  return (
    <ConfirmModal
      open={open}
      onOpenChange={handleClose}
      title={
        codes
          ? t[
              'com.streamlain.settings.account.two-factor.backup-codes.new-title'
            ]()
          : t[
              'com.streamlain.settings.account.two-factor.backup-codes.regenerate-title'
            ]()
      }
      confirmText={
        codes
          ? t['com.streamlain.settings.account.two-factor.backup-codes.done']()
          : t[
              'com.streamlain.settings.account.two-factor.backup-codes.regenerate-confirm'
            ]()
      }
      onConfirm={codes ? () => handleClose(false) : onRegenerate}
      confirmButtonOptions={{ loading: isLoading }}
    >
      {!codes ? (
        <div className={styles.setupStepContent}>
          <p>
            {t[
              'com.streamlain.settings.account.two-factor.backup-codes.description'
            ]()}
          </p>
          <Input
            autoFocus
            type="password"
            placeholder={t[
              'com.streamlain.settings.account.two-factor.password'
            ]()}
            value={password}
            onChange={setPassword}
            onEnter={onRegenerate}
          />
          {error && <p className={styles.errorText}>{error}</p>}
        </div>
      ) : (
        <div className={styles.setupStepContent}>
          <p>
            {t[
              'com.streamlain.settings.account.two-factor.backup-codes.save-hint'
            ]()}
          </p>
          <div className={styles.backupCodesGrid}>
            {codes.map((code, i) => (
              <code key={i} className={styles.backupCode}>
                {code}
              </code>
            ))}
          </div>
          <Button variant="secondary" onClick={copyBackupCodes}>
            {t[
              'com.streamlain.settings.account.two-factor.backup-codes.copy'
            ]()}
          </Button>
        </div>
      )}
    </ConfirmModal>
  );
};

// ─── Main Section ───────────────────────────────────────────────────────────

export const TwoFactorSection = () => {
  const t = useI18n();
  const gql = useService(GraphQLService);
  const [status, setStatus] = useState<TotpStatus | null>(null);
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refresh = useAsyncCallback(async () => {
    setIsRefreshing(true);
    try {
      const [statusRes, devicesRes] = await Promise.all([
        gql.gql({ query: totpStatusQuery }),
        gql.gql({ query: trustedDevicesQuery }),
      ]);
      setStatus(statusRes.totpStatus);
      setDevices(devicesRes.trustedDevices);
    } catch {
      // Silently fail — user may not have 2FA capability
    } finally {
      setIsRefreshing(false);
    }
  }, [gql]);

  // Load on mount
  useState(() => {
    refresh();
  });

  const onToggleMethod = useAsyncCallback(
    async (method: 'totp' | 'email') => {
      try {
        await gql.gql({
          query: setPreferredTwoFactorMethodMutation,
          variables: { method },
        });
        refresh();
        notify.success({
          title:
            t[
              'com.streamlain.settings.account.two-factor.preferred-method.updated'
            ](),
        });
      } catch {
        notify.error({
          title:
            t[
              'com.streamlain.settings.account.two-factor.preferred-method.update-failed'
            ](),
        });
      }
    },
    [gql, refresh, t]
  );

  const onRevokeDevice = useAsyncCallback(
    async (trustId: string) => {
      try {
        await gql.gql({
          query: revokeDeviceTrustMutation,
          variables: { trustId },
        });
        notify.success({
          title:
            t[
              'com.streamlain.settings.account.two-factor.trusted-devices.revoked'
            ](),
        });
        refresh();
      } catch {
        notify.error({
          title:
            t[
              'com.streamlain.settings.account.two-factor.trusted-devices.revoke-failed'
            ](),
        });
      }
    },
    [gql, refresh, t]
  );

  const onRevokeAllTrusts = useAsyncCallback(async () => {
    try {
      const res = await gql.gql({
        query: revokeAllDeviceTrustsMutation,
      });
      notify.success({
        title: t[
          'com.streamlain.settings.account.two-factor.trusted-devices.revoked-all'
        ]({
          count: String(res.revokeAllDeviceTrusts),
        }),
      });
      refresh();
    } catch {
      notify.error({
        title:
          t[
            'com.streamlain.settings.account.two-factor.trusted-devices.revoke-all-failed'
          ](),
      });
    }
  }, [gql, refresh, t]);

  if (isRefreshing && !status) {
    return null; // Don't show section while initial load
  }

  return (
    <>
      <SettingWrapper
        title={t['com.streamlain.settings.account.two-factor.security.title']()}
      >
        <SettingRow
          name={t['com.streamlain.settings.account.two-factor.label']()}
          desc={
            status?.enabled
              ? status.verifiedAt
                ? t['com.streamlain.settings.account.two-factor.enabled-since'](
                    {
                      date: new Date(status.verifiedAt).toLocaleDateString(),
                    }
                  )
                : t['com.streamlain.settings.account.two-factor.enabled']()
              : t['com.streamlain.settings.account.two-factor.description']()
          }
        >
          {status?.enabled ? (
            <Button variant="error" onClick={() => setDisableOpen(true)}>
              {t['com.streamlain.settings.account.two-factor.disable']()}
            </Button>
          ) : (
            <Button variant="primary" onClick={() => setSetupOpen(true)}>
              {t['com.streamlain.settings.account.two-factor.enable']()}
            </Button>
          )}
        </SettingRow>

        {status?.enabled && (
          <>
            <SettingRow
              name={t[
                'com.streamlain.settings.account.two-factor.preferred-method'
              ]()}
              desc={t[
                'com.streamlain.settings.account.two-factor.preferred-method.desc'
              ]()}
            >
              <div className={styles.methodToggle}>
                <Button
                  variant={
                    status.preferredMethod === 'totp' ? 'primary' : 'secondary'
                  }
                  size="default"
                  onClick={() => onToggleMethod('totp')}
                >
                  {t[
                    'com.streamlain.settings.account.two-factor.authenticator'
                  ]()}
                </Button>
                <Button
                  variant={
                    status.preferredMethod === 'email' ? 'primary' : 'secondary'
                  }
                  size="default"
                  onClick={() => onToggleMethod('email')}
                >
                  {t['com.streamlain.settings.account.two-factor.email']()}
                </Button>
              </div>
            </SettingRow>

            <SettingRow
              name={t[
                'com.streamlain.settings.account.two-factor.backup-codes'
              ]()}
              desc={
                status.hasBackupCodes
                  ? t[
                      'com.streamlain.settings.account.two-factor.backup-codes.remaining'
                    ]({ count: String(status.backupCodesCount) })
                  : t[
                      'com.streamlain.settings.account.two-factor.backup-codes.none'
                    ]()
              }
            >
              <Button onClick={() => setRegenerateOpen(true)}>
                {status.hasBackupCodes
                  ? t[
                      'com.streamlain.settings.account.two-factor.backup-codes.regenerate'
                    ]()
                  : t[
                      'com.streamlain.settings.account.two-factor.backup-codes.generate'
                    ]()}
              </Button>
            </SettingRow>

            {devices.length > 0 && (
              <>
                <SettingRow
                  name={t[
                    'com.streamlain.settings.account.two-factor.trusted-devices'
                  ]()}
                  desc={t[
                    'com.streamlain.settings.account.two-factor.trusted-devices.count'
                  ]({
                    count: String(devices.length),
                  })}
                  spreadCol={false}
                >
                  <div className={styles.trustedDevicesList}>
                    {devices.map(device => (
                      <div key={device.id} className={styles.trustedDeviceItem}>
                        <div className={styles.trustedDeviceInfo}>
                          <span className={styles.trustedDeviceLabel}>
                            {device.label ||
                              t[
                                'com.streamlain.settings.account.two-factor.trusted-devices.unknown'
                              ]()}
                          </span>
                          <span className={styles.trustedDeviceExpiry}>
                            {t[
                              'com.streamlain.settings.account.two-factor.trusted-devices.expires'
                            ]({
                              date: new Date(
                                device.expiresAt
                              ).toLocaleDateString(),
                            })}
                          </span>
                        </div>
                        <Button
                          variant="error"
                          size="default"
                          onClick={() => onRevokeDevice(device.id)}
                        >
                          {t[
                            'com.streamlain.settings.account.two-factor.trusted-devices.revoke'
                          ]()}
                        </Button>
                      </div>
                    ))}
                  </div>
                  {devices.length > 1 && (
                    <Button
                      variant="error"
                      onClick={onRevokeAllTrusts}
                      style={{ marginTop: 8 }}
                    >
                      {t[
                        'com.streamlain.settings.account.two-factor.trusted-devices.revoke-all'
                      ]()}
                    </Button>
                  )}
                </SettingRow>
              </>
            )}
          </>
        )}
      </SettingWrapper>

      <TotpSetupDialog
        open={setupOpen}
        onOpenChange={setSetupOpen}
        onComplete={refresh}
      />
      <TotpDisableDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        onComplete={refresh}
      />
      <RegenerateBackupCodesDialog
        open={regenerateOpen}
        onOpenChange={setRegenerateOpen}
        onComplete={refresh}
      />
    </>
  );
};
